import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { SelectField, InputField } from "@/components/ui/FormFields";
import { Modal } from "@/components/ui/Modal";
import {
  listVerificationQueue,
  approveVerificationRequest,
  rejectVerificationRequest,
  requestVerificationInfo,
  type VerifiableEntityType,
  type VerificationRequest,
  type VerificationRequestStatus,
} from "@/api/admin";

export default function VerificationQueueLab() {
  const qc = useQueryClient();
  const [entityType, setEntityType] = useState<VerifiableEntityType | "">("");
  const [status, setStatus] = useState<VerificationRequestStatus | "">(
    "pending",
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-verification-queue", entityType, status],
    queryFn: () =>
      listVerificationQueue({
        entityType: entityType || undefined,
        status: status || undefined,
        limit: 50,
        order: "asc",
      }),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["admin-verification-queue"] });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Verification Queue"
        subtitle="Unified moderation for institutions + event organizers. Ref-interest proposals have their own queue."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Verification Queue" },
        ]}
      />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
          <SelectField
            label="Entity type"
            value={entityType}
            onChange={(e) =>
              setEntityType(e.target.value as VerifiableEntityType | "")
            }
            options={[
              { value: "", label: "All" },
              { value: "institution", label: "Institutions" },
              { value: "event_organizer", label: "Event organizers" },
              { value: "ref_interest_proposal", label: "Interest proposals" },
            ]}
          />
          <SelectField
            label="Status"
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as VerificationRequestStatus | "")
            }
            options={[
              { value: "", label: "All" },
              { value: "pending", label: "Pending" },
              { value: "under_review", label: "Under review" },
              { value: "more_info_requested", label: "More info requested" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
            ]}
          />
        </div>
      </section>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No requests match.</p>
      ) : (
        <ul className="space-y-3">
          {data.data.items.map((r) => (
            <QueueRow key={r.id} req={r} onChange={refresh} />
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueRow({
  req,
  onChange,
}: {
  req: VerificationRequest;
  onChange: () => void;
}) {
  const [modalOpen, setModalOpen] = useState<
    "approve" | "reject" | "request-info" | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleApprove() {
    setBusy(true);
    setError(null);
    try {
      await approveVerificationRequest(req.id, notes || undefined);
      onChange();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    setError(null);
    try {
      await rejectVerificationRequest(req.id, {
        reason,
        notes: notes || undefined,
      });
      onChange();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleRequestInfo() {
    setBusy(true);
    setError(null);
    try {
      await requestVerificationInfo(req.id, notes);
      onChange();
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  function close() {
    setModalOpen(null);
    setNotes("");
    setReason("");
    setError(null);
  }

  const statusVariant: Record<
    VerificationRequestStatus,
    "gray" | "blue" | "green" | "yellow" | "red"
  > = {
    pending: "yellow",
    under_review: "blue",
    more_info_requested: "yellow",
    approved: "green",
    rejected: "red",
  };

  const canAct = req.status === "pending" || req.status === "under_review";

  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="purple">{req.entityType}</Badge>
            <Badge variant={statusVariant[req.status]}>{req.status}</Badge>
          </div>
          <div className="text-xs text-gray-500 mt-2 font-mono">
            req: {req.id.slice(0, 8)}… · entity: {req.entityId.slice(0, 8)}…
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Submitted {new Date(req.submittedAt).toLocaleString()}
            {req.reviewedAt && (
              <>
                {" "}
                · reviewed {new Date(req.reviewedAt).toLocaleString()}
                {req.reviewer && ` by ${req.reviewer.name}`}
              </>
            )}
          </div>
          {req.rejectReason && (
            <p className="text-xs text-red-700 mt-1">
              Rejected: {req.rejectReason}
            </p>
          )}
          {req.reviewNotes && (
            <p className="text-xs text-gray-600 mt-1">Notes: {req.reviewNotes}</p>
          )}
        </div>
        {canAct && (
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalOpen("approve")}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setModalOpen("request-info")}
            >
              Request info
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setModalOpen("reject")}
            >
              Reject
            </Button>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen === "approve"}
        onClose={close}
        title="Approve request"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={handleApprove} isLoading={busy}>
              Approve
            </Button>
          </>
        }
      >
        <InputField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
        {error && <p className="text-sm text-red-700 mt-2">{error}</p>}
      </Modal>

      <Modal
        isOpen={modalOpen === "reject"}
        onClose={close}
        title="Reject request"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              isLoading={busy}
              disabled={!reason.trim()}
            >
              Reject
            </Button>
          </>
        }
      >
        <InputField
          label="Reason"
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={500}
        />
        <InputField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
        {error && <p className="text-sm text-red-700 mt-2">{error}</p>}
      </Modal>

      <Modal
        isOpen={modalOpen === "request-info"}
        onClose={close}
        title="Request more info"
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestInfo}
              isLoading={busy}
              disabled={!notes.trim()}
            >
              Send request
            </Button>
          </>
        }
      >
        <InputField
          label="What info is needed"
          required
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
        {error && <p className="text-sm text-red-700 mt-2">{error}</p>}
      </Modal>
    </li>
  );
}
